import { forwardRef, useEffect, useState } from 'react'
import { Fragment, Node, Schema } from 'prosemirror-model'
import { NodeSelection } from 'prosemirror-state'
import { markdownParser, schema } from 'prose-core'
import { useReactNodeView } from '../ReactNodeView/ReactNodeViewContext'
import { PollSuggestionRequest } from 'common/Prose/nodeViews/AISuggestion/PollSuggestionRequest'
import { useRootStore } from 'stores/RootStore'
import { StreamContentData } from 'common/Prose/nodeViews/AISuggestion/StreamContentData'
import { Transaction } from 'prosemirror-state'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { useIsChannelPostable } from 'components/PostStatusDropdown/utils'

export const addNodeContent = (node: Node, tr: Transaction, schema: Schema, position: number) => {
  const wrappedNode = Node.fromJSON(schema, {
    type: 'doc',
    content: JSON.parse(node.attrs.contentData),
  })

  const replaceTo = position + node.nodeSize
  const content = node.content.size ? node.content : wrappedNode.content
  return tr.replaceWith(position, replaceTo, content)
}

export const AISuggestion = forwardRef<HTMLDivElement, {}>(() => {
  const { nodeView, node, view, getPos } = useReactNodeView()

  const rootStore = useRootStore()
  const { isMobile } = useResponsiveDesign()
  const { isChannelSocialPostable } = useIsChannelPostable()
  const docStore = rootStore.docStore
  const [streamJobId, setStreamJobId] = useState<string | null>(null)
  const [streamContent, setStreamContent] = useState<string>('')

  const isStreaming = !!streamJobId
  const channel = docStore?._almanac_file?.channel
  const shouldStream = !isMobile || !isChannelSocialPostable(channel || undefined)

  useEffect(() => {
    nodeView.ignoreMutation = () => isStreaming
  }, [nodeView, isStreaming])

  useEffect(() => {
    setStreamJobId(null)
  }, [node.attrs.contentData, node.attrs.aiSuggestionRequestId])

  useEffect(() => {
    return () => {
      rootStore.aiGeneration.removeJob(node.attrs.aiSuggestionRequestId)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleAbort = () => {
    setStreamJobId(null)

    const pos = getPos() as number | undefined
    if (!node || node.type !== schema.nodes.ai_suggestion || pos === undefined) {
      return
    }

    if (!streamContent.length) {
      const nodeSelection = new NodeSelection(view.state.doc.resolve(pos))
      const tr = view.state.tr
      tr.setSelection(nodeSelection)
      tr.deleteSelection()
      view.dispatch(tr)

      return
    }

    const partialNode = markdownParser.parse(streamContent)

    const tr = view.state.tr
    tr.replaceWith(pos, pos + node.nodeSize, partialNode?.content || Fragment.empty)
    view.dispatch(tr)
  }
  return (
    <>
      {docStore?.layerId && docStore?.articleId && node.attrs.aiSuggestionRequestId && shouldStream && (
        <PollSuggestionRequest
          onStreaming={(jobId: string) => {
            if (jobId !== streamJobId) {
              setStreamJobId(jobId)
            }
          }}
          view={view}
          onAbort={handleAbort}
          id={node.attrs.aiSuggestionRequestId}
          articleId={docStore.articleId}
          layerId={docStore.layerId}
        />
      )}
      {streamJobId && (
        <StreamContentData
          jobId={streamJobId}
          view={view}
          streamContent={streamContent}
          setStreamContent={setStreamContent}
        />
      )}
    </>
  )
})
